import React, { useState, useRef, useEffect } from "react";
import "./header.css";
import Avatar from "../../../images/Avatar.svg";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "../../../helpers/AccessControlUtils";
import { clearPermissions } from "../../../app/globalSlice";
import {
  capitalizeFirstLetter,
  capitalizeWords,
  getCurrentLanguage,
} from "../../../helpers/Utils";
import { DropdownArrow, FaEnvelope, FaLock, FaGlobe, FaLogout } from "./HeaderSvg";
import ForgotModal from "../../../features/profile/ForgotModal";
import TimezoneModal from "../../../features/profile/TimezoneModal";
import LogoutConfirmationModal from "../../../features/profile/LogoutConfirmationModal";
import Dropdown from "react-bootstrap/Dropdown";
import i18next from "i18next";
import useTranslator from "../../../hooks/useTranslator";
import LanguageToggleSwitch from "../../shared/LanguageToggleSwitch/LanguageToggleSwitch";
import { isValidTimezone, normalizeTimezone, getTimezoneLabelByValue, FULL_TIMEZONE_OPTIONS, LIMITED_TIMEZONE_OPTIONS, isLocalEnvironment } from "../../../helpers/timezoneUtils";
import moment from "moment-timezone";
import { DEFAULT_DATETIME_FORMAT } from "../../../helpers/constants";

import { useUpdateTimezoneMutation } from "../../../features/auth/authApi";
import { setUserTimezone } from "../../../app/globalSlice";
import { showToast } from "../../../helpers/Utils";

const TimezoneInlineSelect = () => {
  const dispatch = useDispatch();
  const userTimezone = useSelector((state) => state.global.userTimezone);
  const [updateTimezone] = useUpdateTimezoneMutation();
  const timezoneOptions = isLocalEnvironment() ? FULL_TIMEZONE_OPTIONS : LIMITED_TIMEZONE_OPTIONS;
  const current = normalizeTimezone(userTimezone) || timezoneOptions[0].value;

  const handleChange = async (e) => {
    const tz = e.target.value;
    try {
      await updateTimezone({ timezone: tz }).unwrap();
      dispatch(setUserTimezone(tz));
      localStorage.setItem("userTimezone", tz);
      window.location.replace(window.location.href);
    } catch {
      showToast("Failed to update timezone", "error");
    }
  };

  return (
    <select
      value={current}
      onChange={handleChange}
      style={{
        border: "1px solid #ee7225",
        borderRadius: "6px",
        padding: "4px 10px",
        fontSize: "13px",
        fontWeight: 600,
        color: "#ee7225",
        background: "#fff",
        cursor: "pointer",
        outline: "none",
      }}
    >
      {timezoneOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
};

const formatCurrentDateTime = (timezone) => {
  if (!timezone || !isValidTimezone(timezone)) {
    return "";
  }

  return moment().tz(timezone).format(DEFAULT_DATETIME_FORMAT);
};

const Header = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const decodedToken = jwtDecode();
  const userTimezone = useSelector((state) => state.global.userTimezone);
  const normalizedTimezone = normalizeTimezone(userTimezone);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isUnreliableTimezone, setIsUnreliableTimezone] = useState(false);
  const [timezoneDateTime, setTimezoneDateTime] = useState(
    formatCurrentDateTime(normalizedTimezone)
  );
  const dropdownRef = useRef(null);
  const [selectedLang, setSelectedLang] = useState(getCurrentLanguage());

  // Check if browser timezone is unreliable
  useEffect(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const unreliable = browserTimezone === "Etc/Greenwich" || (browserTimezone === "UTC" && !userTimezone);
    setIsUnreliableTimezone(unreliable);
  }, [userTimezone]);

  useEffect(() => {
    setTimezoneDateTime(formatCurrentDateTime(normalizedTimezone));

    if (!normalizedTimezone || !isValidTimezone(normalizedTimezone)) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTimezoneDateTime(formatCurrentDateTime(normalizedTimezone));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [normalizedTimezone]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  const handlePasswordChange = (e) => {
    e.stopPropagation();
    setShow(true);
    setForgot(true);
    setDropdownOpen(false);
  };

  const handleTimezoneChange = (e) => {
    e.stopPropagation();
    setShowTimezoneModal(true);
  };

  const handleLogoutClick = (e) => {
    e.stopPropagation();
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    let locale = getCurrentLanguage();
    dispatch(clearPermissions());
    localStorage.clear();
    if (locale) {
      localStorage.setItem("i18nextLng", locale);
    }
    setTimeout(() => {
      navigate("/login", { replace: true });      
    }, 1000);
  };
  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [dropdownOpen]);

  const handleSelect = async (eventKey) => {
    setSelectedLang(eventKey);
    eventKey === "es"
      ? i18next.changeLanguage("es")
      : i18next.changeLanguage("en");
  };

  // console.log(decodedToken)
  return (
    <div className="headerWrapper">
      {forgot && <ForgotModal show={show} setShow={setShow} />}
      <TimezoneModal show={showTimezoneModal} setShow={setShowTimezoneModal} />
      <LogoutConfirmationModal show={showLogoutModal} setShow={setShowLogoutModal} onConfirmLogout={handleConfirmLogout} />
      <div className="d-flex justify-content-between w-100 align-items-center gap-3">
        <div className="header-meta-slot">
          {normalizedTimezone && timezoneDateTime && (
            <div className="header-timezone-panel">              
              <div className="header-timezone-copy">                
                <p className="header-timezone-datetime m-0">
                  <span className="header-timezone-name">{getTimezoneLabelByValue(normalizedTimezone)}</span>
                  <span className="header-timezone-separator"> | </span>
                  <span>{timezoneDateTime}</span>
                </p>
              </div>
            </div>
          )}
        </div>
        <div
          className="header_user"
          ref={dropdownRef}
          onClick={toggleDropdown}
          style={{ cursor: "pointer" }}
        >
          <div className="user_image">
            <img src={Avatar} alt="Avatar" />
          </div>
          <div className="user_info">
            <h5 className="m-0">{decodedToken?.name}</h5>
            <p className="desig m-0">
              {_t(decodedToken?.role_info?.name, capitalizeFirstLetter(capitalizeWords(decodedToken?.role_info?.name || '')) )}
              {/* {capitalizeFirstLetter(capitalizeWords(decodedToken?.role_info?.name || ''))} */}
            </p>
          </div>
          <DropdownArrow isOpen={dropdownOpen} />
          {dropdownOpen && (
            <div className="dropdown_menu">              
              <div className="dropdown_body">
                <div className="dropdown_section">
                  <p className="section_label">{_t("email", "Email")}</p>
                  <span className="icon-text">
                    <FaEnvelope /> {decodedToken?.email}
                  </span>
                </div>

                <div className="dropdown_section align-items-center d-flex justify-content-between gap-3">
                  <p className="section_label">{_t("language", "Language")}</p>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                    <LanguageToggleSwitch />
                  </div>
                </div>

                <div className="dropdown_section align-items-center d-flex justify-content-between gap-3">
                  <p className="section_label">
                    {_t("timezone", "Timezone")}
                    {isUnreliableTimezone && <span style={{ marginLeft: "6px", color: "#ff9800" }}>⚠️</span>}
                  </p>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                    <TimezoneInlineSelect />
                  </div>
                </div>
              </div>

              <div className="dropdown_footer">
                <p className="section_label">{_t("security", "Security")}</p>
                <button className="icon-button footer-button" onClick={handlePasswordChange}>
                  <span className="icon-text">
                    <FaLock /> {_t("change_password", "Change Password")}
                  </span>
                </button>
                <button className="icon-button footer-button" onClick={handleLogoutClick} style={{ marginTop: "8px" }}>
                  <span className="icon-text">
                    <FaLogout /> {_t("log_out", "Logout")}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
